import { use, useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import { isBelowMinimumVersion } from 'shared/utils/version-utils';

import Gateway from 'features/gateway/domain/models/gateway';
import GatewayOverviewRecord from 'features/gateway/domain/models/gateway-overview-record';
import { GatewayType } from 'features/gateway/domain/models/gateway-type';
import { useCreateGateway, useDeleteGateway, useUpdateGateway } from 'features/gateway/domain/queries/gateway-queries';

export interface GatewayDetailsHook {
  viewingMode: ViewingMode;
  mutationIsLoading: boolean;

  currentSelectedGateway: Gateway | undefined;
  selectedSystemType?: GatewayType;
  changeCurrentSelectedGateway: (gateway: Gateway | undefined) => void;

  gatewayPendingDeletion: GatewayOverviewRecord | Gateway | undefined;
  deleteGateway: (gateway: GatewayOverviewRecord | Gateway) => void;

  submitGateway: (gateway: Gateway) => void;

  cancelMutation: () => void;

  isDetailsOpen: boolean;
  openDetailsInCreationMode: () => void;
  openDetailsInViewingMode: (gateway: Gateway) => void;
  openDetailsInEditingMode: (gateway: Gateway) => void;
  closeDetails: (isFormStateDirty: boolean) => void;

  deleteErrorPopupTextKey: string;
  isDeleteErrorPopupOpen: boolean;
  closeDeleteErrorPopup: () => void;

  isDeleteConfirmationPopupOpen: boolean;
  closeDeleteConfirmationPopup: () => void;
  deleteConfirmationAction: () => void;

  isIpAddressPopupOpen: boolean;
  closeIpAddressPopup: () => void;
  ipAddressConfirmationAction: () => void;

  isTogglingSonevoApiDisabled: (gateway: Gateway | undefined) => boolean;
  isTogglingMediaserverDisabled: (gateway: Gateway | undefined) => boolean;
  isChangingSubnetMaskDisabled: (gateway: Gateway | undefined) => boolean;

  form: UseFormReturn<Partial<Gateway>, any>;
}

const useGatewayDetails = (refresh: () => void, refreshGatewayDetails: (gatewayId: string) => void): GatewayDetailsHook => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { showDefaultConfirmationPopUp, enablePopUpGuard } = use(ConfirmationPopupProviderContext);

  const form = useForm({
    mode: 'onBlur',
    defaultValues: {} as Partial<Gateway>,
  });

  const [isDeleteConfirmationPopupOpen, setIsDeleteConfirmationPopupOpen] = useState<boolean>(false);
  const [isDeleteErrorPopupOpen, setIsDeleteErrorPopupOpen] = useState<boolean>(false);
  const [deleteErrorPopupTextKey, setDeleteErrorPopupTextKey] = useState<string>('');
  const [deleteConfirmationAction, setDeleteConfirmationAction] = useState(() => () => {});
  const [isIpAddressPopupOpen, setIsIpAddressPopupOpen] = useState<boolean>(false);
  const [ipAddressConfirmationAction, setIpAddressConfirmationAction] = useState(() => () => {});
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const [viewingMode, setViewingMode] = useState<ViewingMode>('viewing');
  const [currentSelectedGateway, setCurrentSelectedGateway] = useState<Gateway>();
  const [gatewayPendingDeletion, setGatewayPendingDeletion] = useState<GatewayOverviewRecord | Gateway>();

  const {
    mutate: deleteGatewayMutation,
    isSuccess: deleteGatewayIsSuccess,
    isPending: deleteGatewayIsPending,
    isError: deleteGatewayIsError,
    error: deleteGatewayError,
  } = useDeleteGateway();

  const {
    mutate: createGateway,
    isPending: createGatewayIsPending,
    isSuccess: createGatewayIsSuccess,
    isError: createGatewayIsError,
    error: createGatewayError,
    data: createGatewayData,
  } = useCreateGateway();

  const {
    mutate: updateGateway,
    isPending: updateGatewayIsPending,
    isSuccess: updateGatewayIsSuccess,
    isError: updateGatewayIsError,
    error: updateGatewayError,
    variables: updatedGateway,
  } = useUpdateGateway();

  const cidrDefaultValue = 24;

  const setFormValuesWithCurrentGateway = () => {
    form.reset({
      name: currentSelectedGateway?.name ?? '',
      ipAddress: currentSelectedGateway?.ipAddress ?? '',
      cidr: currentSelectedGateway?.cidr ?? getDefaultCidrValue(currentSelectedGateway?.type),
      note: currentSelectedGateway?.note ?? '',
      type: currentSelectedGateway?.type ?? GatewayType.Unknown,
      organisationUnitId: currentSelectedGateway?.organisationUnitId ?? null,
      shouldCreateDevicesAutomatically: currentSelectedGateway?.shouldCreateDevicesAutomatically ?? false,
      shouldMonitorMediaserver: currentSelectedGateway?.shouldMonitorMediaserver ?? false,
      offlinePortalUsername: currentSelectedGateway?.offlinePortalUsername ?? '',
      offlinePortalPassword: currentSelectedGateway?.offlinePortalPassword ?? '',
      childGateways: currentSelectedGateway?.childGateways ?? [],
      kadexIpAddress: currentSelectedGateway?.kadexIpAddress ?? '',
      kadexUsername: currentSelectedGateway?.kadexUsername ?? '',
      kadexPassword: currentSelectedGateway?.kadexPassword ?? '',
      isSonevoApiEnabled: currentSelectedGateway?.isSonevoApiEnabled ?? false,
      isAlarmmonitorEnabled: currentSelectedGateway?.isAlarmmonitorEnabled ?? false,
      isAudioEnabled: currentSelectedGateway?.isAudioEnabled ?? false,
      isMediaserverEnabled: currentSelectedGateway?.isMediaserverEnabled ?? false,
      isKadexApiEnabled: currentSelectedGateway?.isKadexApiEnabled ?? false,
      isIS32ApiEnabled: currentSelectedGateway?.isIS32ApiEnabled ?? false,
      isIPUnitApiEnabled: currentSelectedGateway?.isIPUnitApiEnabled ?? false,
      isDomipApiEnabled: currentSelectedGateway?.isDomipApiEnabled ?? false,
      isRetryMechanismEnabled: currentSelectedGateway?.isRetryMechanismEnabled ?? false,
      isWanderingPreventionApiEnabled: currentSelectedGateway?.isWanderingPreventionApiEnabled ?? false,
      isIntercomModuleEnabled: currentSelectedGateway?.isIntercomModuleEnabled ?? false,
    });
  };

  const sonevoApiEnabledWatch = form.watch('isSonevoApiEnabled');
  const selectedSystemType = form.watch('type');
  const hasSelectedType = GatewayType.Unknown !== selectedSystemType;

  useEffect(() => {
    if (!sonevoApiEnabledWatch) {
      form.setValue('isIS32ApiEnabled', false);
      form.setValue('isIPUnitApiEnabled', false);
      form.setValue('isDomipApiEnabled', false);
      form.setValue('isWanderingPreventionApiEnabled', false);
      form.setValue('isIntercomModuleEnabled', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sonevoApiEnabledWatch]);

  useEffect(() => {
    if (hasSelectedType && !currentSelectedGateway) {
      resetForm(selectedSystemType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSystemType]);

  useEffect(() => {
    setFormValuesWithCurrentGateway();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedGateway]);

  useEffect(() => {
    enablePopUpGuard(form.formState.isDirty);
  }, [form.formState.isDirty]);

  useEffect(() => {
    if (createGatewayIsSuccess && createGatewayData) {
      setCurrentSelectedGateway(createGatewayData);
      setViewingMode('viewing');
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createGatewayIsSuccess]);

  useEffect(() => {
    if (updateGatewayIsSuccess && updatedGateway?.id) {
      setViewingMode('viewing');
      refresh();
      refreshGatewayDetails(updatedGateway.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateGatewayIsSuccess]);

  useEffect(() => {
    if (createGatewayIsError && createGatewayError) {
      setErrorMessage(createGatewayError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createGatewayIsError]);

  useEffect(() => {
    if (updateGatewayIsError && updateGatewayError) {
      setErrorMessage(updateGatewayError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateGatewayIsError]);

  useEffect(() => {
    if (deleteGatewayIsError && deleteGatewayError) {
      setErrorMessage(deleteGatewayError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteGatewayIsError]);

  useEffect(() => {
    if (deleteGatewayIsSuccess) {
      setIsDeleteConfirmationPopupOpen(false);
      setIsDetailsOpen(false);
      setGatewayPendingDeletion(undefined);
      setCurrentSelectedGateway(undefined);
      refresh();
    }
  }, [deleteGatewayIsSuccess]);

  const mutationIsLoading =
    form.formState.isSubmitting ||
    form.formState.isValidating ||
    updateGatewayIsPending ||
    createGatewayIsPending ||
    deleteGatewayIsPending;

  const changeCurrentSelectedGateway = (gateway: Gateway | undefined) => {
    setCurrentSelectedGateway(gateway);
  };

  const submitGateway = (gateway: Gateway) => {
    const hasChangedIpAddress =
      (currentSelectedGateway?.type === GatewayType.IoTEdge ||
        currentSelectedGateway?.type === GatewayType.IoTEdgeMediaServerGatewayCluster ||
        currentSelectedGateway?.type === GatewayType.IoTEdgeCluster) &&
      currentSelectedGateway?.id &&
      currentSelectedGateway?.ipAddress !== gateway.ipAddress?.trim();

    if (hasChangedIpAddress) {
      setIsIpAddressPopupOpen(true);

      setIpAddressConfirmationAction(() => () => {
        saveGateway(gateway);
        setIsIpAddressPopupOpen(false);
      });
      return;
    }

    saveGateway(gateway);
  };

  const saveGateway = (gateway: Gateway) => {
    if (viewingMode === 'creation') {
      createGateway(gateway);
    } else {
      updateGateway(gateway);
    }
  };

  const deleteGateway = (gateway: GatewayOverviewRecord | Gateway) => {
    setGatewayPendingDeletion(gateway);
    if (!gateway.canBeDeleted) {
      setDeleteErrorPopupTextKey('linkedDevicesOrSystemsBodyText');
      setIsDeleteErrorPopupOpen(true);
      return;
    }

    setIsDeleteConfirmationPopupOpen(true);
    setDeleteConfirmationAction(() => () => confirmDeleteGateway(gateway));
  };

  const confirmDeleteGateway = (gateway: GatewayOverviewRecord | Gateway) => {
    if (gateway.id) {
      deleteGatewayMutation(gateway.id);
    }
    setDeleteConfirmationAction(() => () => {});
  };

  const openDetailsInCreationMode = () => {
    setCurrentSelectedGateway(undefined);
    setIsDetailsOpen(true);
    setViewingMode('creation');
  };

  const openDetailsInEditingMode = (gateway: Gateway | undefined) => {
    setCurrentSelectedGateway(gateway);
    setIsDetailsOpen(true);
    setViewingMode('editing');
    setFormValuesWithCurrentGateway();
  };

  const openDetailsInViewingMode = (gateway: Gateway | undefined) => {
    setCurrentSelectedGateway(gateway);
    setIsDetailsOpen(true);
    setViewingMode('viewing');
  };

  const closeDetails = (isFormStateDirty: boolean) => {
    showDefaultConfirmationPopUp({
      shouldShow: (viewingMode === 'creation' || viewingMode === 'editing') && isFormStateDirty,
      action: () => {
        setIsDetailsOpen(false);
        setCurrentSelectedGateway(undefined);
        resetForm();
      },
    });
  };

  const cancelMutation = () => {
    if (viewingMode === 'creation') {
      closeDetails(false);
    } else if (viewingMode === 'editing') {
      setViewingMode('viewing');
    }
    setFormValuesWithCurrentGateway();
  };

  const closeDeleteConfirmationPopup = () => {
    setIsDeleteConfirmationPopupOpen(false);
    setGatewayPendingDeletion(undefined);
  };

  const closeDeleteErrorPopup = () => {
    setIsDeleteErrorPopupOpen(false);
    setGatewayPendingDeletion(undefined);
  };

  const closeIpAddressPopup = () => {
    setIsIpAddressPopupOpen(false);
  };

  const getDefaultCidrValue = (type: GatewayType | undefined): number | undefined => {
    return type == GatewayType.IoTEdge || type == GatewayType.IoTEdgeCluster || type == GatewayType.IoTEdgeMediaServerGatewayCluster
      ? cidrDefaultValue
      : undefined;
  };

  const resetForm = (type?: GatewayType) => {
    form.reset({
      name: '',
      ipAddress: '',
      cidr: getDefaultCidrValue(type),
      note: '',
      type: type ?? GatewayType.Unknown,
      organisationUnitId: undefined,
      shouldCreateDevicesAutomatically: false,
      shouldMonitorMediaserver: false,
      isSonevoApiEnabled: false,
      isAlarmmonitorEnabled: false,
      isAudioEnabled: false,
      isMediaserverEnabled: false,
      isKadexApiEnabled: false,
      isIS32ApiEnabled: false,
      isIPUnitApiEnabled: false,
      isDomipApiEnabled: false,
      offlinePortalUsername: '',
      offlinePortalPassword: '',
      childGateways: [],
      kadexIpAddress: '',
      kadexUsername: '',
      kadexPassword: '',
      isRetryMechanismEnabled: false,
      isWanderingPreventionApiEnabled: false,
      isIntercomModuleEnabled: false,
    });
  };

  const isTogglingSonevoApiDisabled = (gateway: Gateway | undefined): boolean => {
    const minimumVersion = '1.28.0';

    return (
      !gateway ||
      (gateway.type === GatewayType.IoTEdgeCluster &&
        gateway.childGateways?.some((x) => isBelowMinimumVersion(x.hostModuleVersion, minimumVersion))) ||
      (gateway.type === GatewayType.IoTEdge && isBelowMinimumVersion(gateway.hostModuleVersion, minimumVersion))
    );
  };

  const isTogglingMediaserverDisabled = (gateway: Gateway | undefined): boolean => {
    const minimumVersion = '1.36.0';

    return (
      !gateway ||
      (gateway.type === GatewayType.IoTEdgeCluster &&
        gateway.childGateways
          ?.filter((x) => x.name?.trim() !== '')
          .some((x) => !x.imageVersion || isBelowMinimumVersion(x.imageVersion, minimumVersion))) ||
      (gateway.type === GatewayType.IoTEdge && !gateway.imageVersion) ||
      isBelowMinimumVersion(gateway.imageVersion, minimumVersion)
    );
  };

  const isChangingSubnetMaskDisabled = (gateway: Gateway | undefined): boolean => {
    const minimumVersion = '1.56.0';

    return (
      !gateway ||
      ((gateway.type === GatewayType.IoTEdgeCluster || gateway.type === GatewayType.IoTEdgeMediaServerGatewayCluster) &&
        gateway.childGateways
          ?.filter((x) => x.name?.trim() !== '')
          .some((x) => !x.imageVersion || isBelowMinimumVersion(x.imageVersion, minimumVersion))) ||
      (gateway.type === GatewayType.IoTEdge && !gateway.imageVersion) ||
      isBelowMinimumVersion(gateway.imageVersion, minimumVersion)
    );
  };

  return {
    viewingMode,
    mutationIsLoading,

    currentSelectedGateway,
    changeCurrentSelectedGateway,
    selectedSystemType,

    gatewayPendingDeletion,
    deleteGateway,

    submitGateway,

    cancelMutation,

    isDetailsOpen,
    openDetailsInCreationMode,
    openDetailsInEditingMode,
    openDetailsInViewingMode,
    closeDetails,

    deleteErrorPopupTextKey,
    isDeleteErrorPopupOpen,
    closeDeleteErrorPopup,

    isDeleteConfirmationPopupOpen,
    closeDeleteConfirmationPopup,
    deleteConfirmationAction,

    isIpAddressPopupOpen,
    closeIpAddressPopup,
    ipAddressConfirmationAction,

    isTogglingSonevoApiDisabled,
    isTogglingMediaserverDisabled,
    isChangingSubnetMaskDisabled,

    form,
  };
};

export default useGatewayDetails;
